import { Slot } from '@radix-ui/react-slot'
import { motion } from 'motion/react'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { Spinner } from './spinner'
import { AnimatePresence } from 'motion/react'

const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`,
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-11 px-4 py-2',
        icon: 'size-11',
        lg: 'h-11 rounded-md px-8',
        sm: 'h-9 rounded-md px-3',
        inline: 'h-auto p-0 text-base',
      },
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-brand-funkia font-semibold underline-offset-4 hover:underline',
        outline:
          'border border-subtle-01 bg-layer-1 hover:border-accent hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-layer-2 text-text-primary hover:bg-layer-3',
        neutral:
          'bg-black dark:bg-white text-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200',
        background: 'bg-layer-2 text-text-primary hover:bg-layer-3',
      },
    },
  },
)

export type ButtonProps = {
  asChild?: boolean
  isLoading?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, isLoading, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
      )
    }

    return (
      <button
        className={cn(buttonVariants({ className, size, variant }))}
        ref={ref}
        {...props}
        onClick={e => {
          if (isLoading) return
          props.onClick?.(e)
        }}
      >
        <AnimatePresence>
          {isLoading && (
            <motion.div
              className="overflow-hidden"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Spinner />
            </motion.div>
          )}
        </AnimatePresence>
        {props.children}
      </button>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
